// 🔥 IMMEDIATE DEBUG LOG TO CHECK IF CANVAS IS LOADED
console.log('🔥🔥🔥 Canvas.tsx FILE LOADED');

import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Hand, MousePointer, Minus, Plus, PanelRight, PanelRightClose, RotateCcw, Maximize2, Minimize2 } from 'lucide-react';
import CanvasToolbar from './CanvasToolbar';
import WidgetLayer from './WidgetLayer';
import ContextMenu from './ContextMenu';
import CanvasSidebar from './CanvasSidebar';
import CanvasBottomBar from './CanvasBottomBar';
import TemplatesPopup from './TemplatesPopup';
import OnboardingGuide from '../OnboardingGuide/OnboardingGuide';
import { CanvasWidget, Position, ToolType, WidgetGroup } from './types';
import WidgetGroupManager from './WidgetGroupManager';
import { Token } from '../../types/supabase';
import {
  createAdvancedChartsWidget,
  createOrderBookWidget,
  createTradesBuySellWidget,
  createTokenInfoWidget,
  createSniperBotWidget,
  createAdvancedAutoTraderWidget,
  createCopyTraderWidget,
  createProfitAndLossWidget,
  createActivePositionsWidget,
  createTokenStatsWidget
} from '../../widgets';
import {
  shouldShowOnboarding,
  markOnboardingCompleted,
  markOnboardingSkipped,
  resetOnboardingPreferences
} from '../../services/onboardingService';

// Define public interface for Canvas component
export interface CanvasRef {
  addWidget: (widget: Partial<CanvasWidget>) => void;
}

const BackgroundParticles: React.FC = () => {
  const particlesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const createBackgroundParticles = () => {
      if (!particlesRef.current) return;

      while (particlesRef.current.firstChild) {
        particlesRef.current.removeChild(particlesRef.current.firstChild);
      }

      const container = document.createElement('div');
      container.className = 'absolute inset-0 overflow-hidden pointer-events-none';

      // Create green particles
      for (let i = 0; i < 10; i++) {
        const particle = document.createElement('div');
        const size = Math.random() * 4 + 2;

        particle.className = 'absolute rounded-full bg-[#47be8f] opacity-10';
        particle.style.width = `${size}px`;
        particle.style.height = `${size}px`;
        particle.style.top = `${Math.random() * 100}%`;
        particle.style.left = `${Math.random() * 100}%`;
        particle.style.animation = `float ${Math.random() * 15 + 10}s linear infinite`;
        particle.style.animationDelay = `-${Math.random() * 10}s`;

        container.appendChild(particle);
      }

      // Create gold particles
      for (let i = 0; i < 5; i++) {
        const particle = document.createElement('div');
        const size = Math.random() * 3 + 1;

        particle.className = 'absolute rounded-full bg-[#c19411] opacity-10';
        particle.style.width = `${size}px`;
        particle.style.height = `${size}px`;
        particle.style.top = `${Math.random() * 100}%`;
        particle.style.left = `${Math.random() * 100}%`;
        particle.style.animation = `float ${Math.random() * 15 + 10}s linear infinite`;
        particle.style.animationDelay = `-${Math.random() * 10}s`;

        container.appendChild(particle);
      }

      particlesRef.current.appendChild(container);
    };

    createBackgroundParticles();
  }, []);
  
  return <div ref={particlesRef} className="absolute inset-0 z-0"></div>;
};

const Canvas = forwardRef<CanvasRef>((props, ref) => {
  // 🔥 IMMEDIATE DEBUG LOG TO CHECK IF CANVAS COMPONENT IS RENDERING
  console.log('🔥🔥🔥 Canvas COMPONENT IS RENDERING');
  
  const [widgets, setWidgets] = useState<CanvasWidget[]>([]);
  const [widgetGroups, setWidgetGroups] = useState<WidgetGroup[]>([]);
  const [tool, setTool] = useState<ToolType>('select');
  const [zoom, setZoom] = useState(1);
  const [panOffset, setPanOffset] = useState<Position>({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<Position>({ x: 0, y: 0 });
  const [contextMenu, setContextMenu] = useState<{
    isOpen: boolean;
    position: Position;
  }>({
    isOpen: false,
    position: { x: 0, y: 0 }
  });
  const [showSidebar, setShowSidebar] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showGroupManager, setShowGroupManager] = useState(false);
  const [selectedWidgetIds, setSelectedWidgetIds] = useState<string[]>([]);
  const [isHoveringWidget, setIsHoveringWidget] = useState(false);
  const [showTemplatesPopup, setShowTemplatesPopup] = useState(false);
  const [maxZIndex, setMaxZIndex] = useState(1);
  
  // Onboarding state
  const [showOnboarding, setShowOnboarding] = useState(false);
  
  const canvasRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // DEBUG: Log sidebar state
  console.log('🔥🔥🔥 Canvas showSidebar state:', showSidebar);
  console.log('🔥🔥🔥 Canvas isExpanded state:', isExpanded);

  // Initialize onboarding on first render
  useEffect(() => {
    // Check if user should see onboarding guide
    if (shouldShowOnboarding()) {
      // Delay showing onboarding to ensure canvas is fully loaded
      const timer = setTimeout(() => {
        setShowOnboarding(true);
      }, 1500);
      
      return () => clearTimeout(timer);
    }
  }, []);

  // Onboarding handlers
  const handleOnboardingClose = () => {
    setShowOnboarding(false);
    markOnboardingCompleted();
  };

  const handleOnboardingSkipAll = () => {
    setShowOnboarding(false);
    markOnboardingSkipped();
  };

  const handleReplayOnboarding = () => {
    resetOnboardingPreferences();
    setShowOnboarding(true);
  };

  // Update setZoom and setPanOffset
  const handleZoomChange = useCallback((newZoom: number) => {
    setZoom(newZoom);
  }, []);

  const handlePanOffsetChange = useCallback((newPanOffset: Position) => {
    setPanOffset(newPanOffset);
  }, []);

  // Listen for onboarding zoom events
  useEffect(() => {
    const handleOnboardingZoom = (event: CustomEvent<{ zoom: number }>) => {
      console.log('🎯 Canvas: Received onboarding zoom event', event.detail.zoom);
      handleZoomChange(event.detail.zoom);
    };

    const handleOnboardingZoomRestore = () => {
      console.log('🎯 Canvas: Restoring zoom to 100%');
      handleZoomChange(1.0);
      handlePanOffsetChange({ x: 0, y: 0 });
    };

    window.addEventListener('ekko:onboarding_zoom', handleOnboardingZoom as EventListener);
    window.addEventListener('ekko:onboarding_zoom_restore', handleOnboardingZoomRestore);

    return () => {
      window.removeEventListener('ekko:onboarding_zoom', handleOnboardingZoom as EventListener);
      window.removeEventListener('ekko:onboarding_zoom_restore', handleOnboardingZoomRestore);
    };
  }, [handleZoomChange, handlePanOffsetChange]);

  // Function to bring a widget to the front
  const bringWidgetToFront = useCallback((widgetId: string) => {
    setWidgets(prevWidgets => {
      const newMaxZIndex = maxZIndex + 1;
      setMaxZIndex(newMaxZIndex);
      
      return prevWidgets.map(widget => 
        widget.id === widgetId 
          ? { ...widget, zIndex: newMaxZIndex }
          : widget
      );
    });
  }, [maxZIndex]);

  // Function to sync tokens across widgets
  const handleSyncTokens = useCallback(() => {
    console.log('🔄 SYNC DEBUG: Starting token sync...');
    console.log('🔄 SYNC DEBUG: Current widgets:', widgets.map(w => ({ 
      id: w.id, 
      type: w.type, 
      hasToken: !!w.selectedToken,
      tokenSymbol: w.selectedToken?.symbol 
    })));
    
    // Find the first widget that has a selected token
    const widgetWithToken = widgets.find(widget => widget.selectedToken);
    
    if (!widgetWithToken) {
      console.log('🔄 SYNC DEBUG: No widget with a selected token found');
      // Show toast notification
      window.dispatchEvent(new CustomEvent('ekko:show_toast', {
        detail: {
          message: 'No widget with a selected token found to sync',
          type: 'error',
          id: `sync-error-${Date.now()}`
        }
      }));
      return;
    }

    const tokenToSync = widgetWithToken.selectedToken;
    const widgetsToUpdate = widgets.filter(w => !w.selectedToken && !w.isDeleted);
    
    console.log(`🔄 SYNC DEBUG: Found token to sync: ${tokenToSync?.symbol}`);
    console.log(`🔄 SYNC DEBUG: Widgets to update: ${widgetsToUpdate.length}`);
    console.log('🔄 SYNC DEBUG: Widgets to update details:', widgetsToUpdate.map(w => ({ 
      id: w.id, 
      type: w.type 
    })));
    
    if (widgetsToUpdate.length === 0) {
      // Show toast notification
      window.dispatchEvent(new CustomEvent('ekko:show_toast', {
        detail: {
          message: 'All widgets already have tokens selected',
          type: 'error',
          id: `sync-info-${Date.now()}`
        }
      }));
      return;
    }

    console.log(`🔄 Syncing token ${tokenToSync?.symbol} across ${widgetsToUpdate.length} widgets`);

    // Update all widgets that don't have a selected token
    setWidgets(prevWidgets => {
      const updatedWidgets = prevWidgets.map(widget => {
        if (!widget.selectedToken && !widget.isDeleted) {
          console.log(`🔄 SYNC DEBUG: Updating widget ${widget.id} (${widget.type}) with token ${tokenToSync?.symbol}`);
          
          // Create updated widget with token and updated content
          const updatedWidget = {
            ...widget,
            selectedToken: tokenToSync,
            content: React.cloneElement(widget.content as React.ReactElement, { 
              selectedToken: tokenToSync,
              key: `${widget.id}-${tokenToSync?.mint}-sync`
            })
          };
          
          return updatedWidget;
        }
        return widget;
      });
      
      console.log('🔄 SYNC DEBUG: Updated widgets result:', updatedWidgets.map(w => ({ 
        id: w.id, 
        type: w.type, 
        hasToken: !!w.selectedToken,
        tokenSymbol: w.selectedToken?.symbol 
      })));
      
      return updatedWidgets;
    });

    // Show success notification
    window.dispatchEvent(new CustomEvent('ekko:show_toast', {
      detail: {
        message: `🔄 Synced ${tokenToSync?.symbol} token across ${widgetsToUpdate.length} widgets`,
        type: 'success',
        id: `sync-success-${Date.now()}`
      }
    }));
  }, [widgets]);

  // Custom scrollbar style and template switching styles (inject once)
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      /* Custom scrollbar for canvas components */
      .custom-scrollbar::-webkit-scrollbar {
        width: 6px;
        height: 6px;
      }
      
      .custom-scrollbar::-webkit-scrollbar-track {
        background: rgba(30, 40, 40, 0.2);
        border-radius: 3px;
      }
      
      .custom-scrollbar::-webkit-scrollbar-thumb {
        background-color: rgba(71, 190, 143, 0.3);
        border-radius: 3px;
        transition: all 0.2s;
      }
      
      .custom-scrollbar::-webkit-scrollbar-thumb:hover {
        background-color: rgba(71, 190, 143, 0.5);
      }

      /* Enhanced interactive element handling */
      .widget input, .widget button, .widget select, .widget textarea, 
      .widget [role="slider"], .widget .slider, .widget [type="range"], .widget .toggle-slider {
        pointer-events: auto !important;
        touch-action: auto !important;
      }

      /* Prevent canvas interactions on widget content */
      .widget {
        pointer-events: auto;
      }

      .widget * {
        user-select: auto;
      }

      /* Specific handling for range inputs and sliders */
      .widget input[type="range"], .widget .slider, .widget .toggle-slider {
        -webkit-appearance: none;
        cursor: pointer;
        pointer-events: auto !important;
      }

      .widget input[type="range"]::-webkit-slider-thumb {
        pointer-events: auto !important;
        cursor: grab;
      }

      .widget input[type="range"]::-webkit-slider-thumb:active {
        cursor: grabbing;
      }

      /* Ensure form elements can receive focus */
      .widget input:focus, .widget select:focus, .widget textarea:focus {
        outline: 2px solid rgba(71, 190, 143, 0.5);
        outline-offset: 1px;
      }

    `;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  // Handle mouse events for canvas interaction
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;

      handlePanOffsetChange({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);

      if (canvasRef.current) {
        canvasRef.current.style.cursor = tool === 'hand' ? 'grab' : 'default';
      }
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStart, tool, handlePanOffsetChange]);

  // Robust hover state management with timeout-based reset
  const setIsHoveringWidgetWithTimeout = useCallback((isHovering: boolean) => {
    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    
    setIsHoveringWidget(isHovering);
    
    // If we're setting hover to true, set a safety timeout to reset it
    if (isHovering) {
      hoverTimeoutRef.current = setTimeout(() => {
        console.log('🔧 Safety timeout: Resetting hover state to allow zooming');
        setIsHoveringWidget(false);
        hoverTimeoutRef.current = null;
      }, 5000); // Increased to 5 seconds for better reliability
    }
  }, []);

  // Reset hover state when certain interactions occur
  const resetHoverState = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setIsHoveringWidget(false);
  }, []);

  // Handle wheel events for zooming
  const handleWheel = useCallback((e: WheelEvent) => {
    // Check if we're hovering over a widget
    if (isHoveringWidget) {
      console.log('🚫 Zoom prevented: Currently hovering over a widget');
      return;
    }

    // Additional check: see if the event target is within a widget or interactive element
    const target = e.target as HTMLElement;
    const isInWidget = target.closest('.widget') !== null;
    const isInteractiveElement = target.matches('input, button, select, textarea, [role="slider"], .slider, [type="range"], .toggle-slider') ||
                                target.closest('input, button, select, textarea, [role="slider"], .slider, [type="range"], .toggle-slider') !== null;
    
    if (isInWidget || isInteractiveElement) {
      console.log('🚫 Zoom prevented: Event target is within a widget or interactive element');
      return;
    }

    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    e.preventDefault();

    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    const mouseXOnCanvas = (mouseX - panOffset.x) / zoom;
    const mouseYOnCanvas = (mouseY - panOffset.y) / zoom;

    const zoomFactor = e.deltaY < 0 ? 1.05 : 0.95;
    const newZoom = Math.max(0.1, Math.min(5, zoom * zoomFactor));

    const newPanOffsetX = mouseX - mouseXOnCanvas * newZoom;
    const newPanOffsetY = mouseY - mouseYOnCanvas * newZoom;

    handleZoomChange(newZoom);
    handlePanOffsetChange({ x: newPanOffsetX, y: newPanOffsetY });
  }, [zoom, panOffset, isHoveringWidget, handleZoomChange, handlePanOffsetChange]);

  // Add wheel event listener with passive: false to allow preventDefault
  useEffect(() => {
    const canvasElement = canvasRef.current;
    if (!canvasElement) return;

    canvasElement.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      canvasElement.removeEventListener('wheel', handleWheel);
      // Cleanup hover timeout on unmount
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, [handleWheel]);

  // Add a useEffect to listen for token selection events
  useEffect(() => {
    const handleTokenSelected = (event: CustomEvent<{ token: Token; force_refresh?: boolean }>) => {
      console.log('🚨 TOKEN SELECTION EVENT DEBUG START 🚨');
      const { token, force_refresh } = event.detail;
      
      console.log('🎯 Token selection event received:', token.symbol, token.mint);
      console.log('🎯 Current widgets on canvas:', widgets.length);
      console.log('🎯 Widget details:', widgets.map(w => ({ id: w.id, type: w.type, hasToken: !!w.selectedToken })));
      
      // Find existing widgets that should be updated with the selected token
      const widgetsToUpdate = ['order-book', 'Chart', 'TokenTransactions', 'token-info-widget', 'trades-buy-sell', 'advanced-charts', 'token-charts-widget', 'token-stats', 'whale-alerts-widget'];
      
      // Check if we have any of these widgets on the canvas
      const existingWidgets = widgets.filter(widget => {
        const widgetType = widget.type;
        const shouldUpdate = widgetsToUpdate.some(type => 
          // Exact match or type includes the search term
          widgetType === type || widgetType.includes(type)
        ) && !widget.isDeleted;
        
        if (shouldUpdate) {
          console.log(`✅ Found widget to update: ${widget.type} with ID ${widget.id}`);
        }
        
        return shouldUpdate;
      });
      
      console.log('📊 All widget types on canvas:', widgets.map(w => `${w.type} (${w.id})`).join(', '));
      console.log('🎯 Widget types that should be updated:', widgetsToUpdate.join(', '));
      console.log('✅ Found widgets to update:', existingWidgets.map(w => `${w.type} (${w.id})`).join(', '));
      console.log('🎯 Token being used for updates:', token);

      if (existingWidgets.length > 0) {
        // Update existing widgets with the new token
        console.log(`🔄 Updating ${existingWidgets.length} existing widgets with token: ${token.symbol}`);
        
        setWidgets(prevWidgets => {
          console.log('🔄 INSIDE setWidgets - prevWidgets count:', prevWidgets.length);
          
          const updatedWidgets = prevWidgets.map(widget => {
            // Only update widgets of specific types that aren't deleted
            const widgetType = widget.type;
            const shouldUpdate = widgetsToUpdate.some(type => 
              // Exact match or type includes the search term
              widgetType === type || widgetType.includes(type)
            ) && !widget.isDeleted;
            
            if (shouldUpdate) {
              console.log(`🔄 Updating widget ${widget.id} of type ${widget.type} with token ${token.symbol} (${token.mint})`);
              
              // Create a clean token object with all necessary properties
              const cleanToken = {
                ...token,
                id: token.id || token.mint,
                mint: token.mint,
                name: token.name || token.symbol,
                symbol: token.symbol,
                image_uri: token.image_uri || null
              };
              
              console.log(`💾 WIDGET UPDATE: Setting selectedToken on widget ${widget.id}:`, cleanToken);
              
              // Return updated widget with selectedToken property AND updated content
              const updatedWidget = {
                ...widget,
                selectedToken: cleanToken, // THIS IS CRITICAL - Store token data on the widget itself
                content: React.cloneElement(widget.content as React.ReactElement, { 
                  selectedToken: cleanToken,
                  key: `${widget.id}-${token.mint}` // Use stable key without timestamp
                })
              };
              
              console.log(`💾 UPDATED WIDGET RESULT:`, {
                id: updatedWidget.id,
                type: updatedWidget.type,
                hasSelectedToken: !!updatedWidget.selectedToken,
                selectedTokenMint: updatedWidget.selectedToken?.mint
              });
              
              return updatedWidget;
            }
            return widget;
          });
          
          console.log(`💾 Updated widgets summary:`, updatedWidgets.map(w => ({ 
            id: w.id, 
            type: w.type, 
            hasToken: !!w.selectedToken,
            tokenMint: w.selectedToken?.mint 
          })));
          
          console.log('🚨 TOKEN SELECTION EVENT DEBUG END 🚨');
          return updatedWidgets;
        });
      } else {
        // No existing widgets to update, but we don't want to create new ones
        console.log(`ℹ️ No existing widgets found for token: ${token.symbol}. Add widgets from the sidebar first.`);
        console.log('🚨 TOKEN SELECTION EVENT DEBUG END - NO WIDGETS 🚨');
        // Do nothing - don't create new widgets automatically
      }
    };

    console.log('🔧 TOKEN SELECTION LISTENER: Setting up event listeners...');
    
    // Add event listener with type assertion
    window.addEventListener('ekko:token_selected', handleTokenSelected as EventListener);
    
    // Also listen for the canvas_update_widgets event
    window.addEventListener('ekko:canvas_update_widgets', handleTokenSelected as EventListener);
    
    console.log('🔧 TOKEN SELECTION LISTENER: Event listeners added');
    
    // Clean up
    return () => {
      console.log('🔧 TOKEN SELECTION LISTENER: Cleaning up event listeners...');
      window.removeEventListener('ekko:token_selected', handleTokenSelected as EventListener);
      window.removeEventListener('ekko:canvas_update_widgets', handleTokenSelected as EventListener);
    };
  }, [widgets]);

  // Auto-sync tokens when new widgets are added
  useEffect(() => {
    // Find widgets without tokens
    const widgetsWithoutTokens = widgets.filter(w => !w.selectedToken && !w.isDeleted);
    // Find widgets with tokens
    const widgetsWithTokens = widgets.filter(w => w.selectedToken && !w.isDeleted);
    
    if (widgetsWithoutTokens.length > 0 && widgetsWithTokens.length > 0) {
      console.log('🔄 AUTO-SYNC: Found widgets without tokens, auto-syncing...');
      
      // Use a timeout to ensure the widget state has settled
      const timeoutId = setTimeout(() => {
        handleSyncTokens();
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [widgets.length, handleSyncTokens]); // Trigger when widget count changes

  const visibleWidgets = widgets.filter(widget => !widget.isDeleted);

  // Match the grid settings from index.css: --grid-size: 40px, scaled with zoom
  const baseGridSize = 40; // From the CSS var --grid-size
  const gridSize = Math.floor(baseGridSize * zoom);

  useImperativeHandle(ref, () => ({
    addWidget: (widget: Partial<CanvasWidget>) => {
      
      // Make sure content is a valid React element
      const widgetContent = widget.content || (
        <div className="flex items-center justify-center h-full text-white">
          No content provided
        </div>
      );

      const newZIndex = maxZIndex + 1;
      const newWidget: CanvasWidget = {
        id: uuidv4(),
        type: widget.type || 'default',
        title: widget.title || 'Untitled Widget',
        content: widgetContent,
        position: widget.position || { x: 100, y: 100 },
        size: widget.size || { width: 350, height: 300 },
        zIndex: newZIndex,
        isDeleted: false,
        groupId: widget.groupId,
        isParentWidget: widget.isParentWidget,
        selectedToken: widget.selectedToken || undefined
      };
      
      setWidgets(prevWidgets => [...prevWidgets, newWidget]);
      setMaxZIndex(newZIndex);
    }
  }));

  return (
    <div className="h-[calc(100vh-3.5rem)] flex flex-col overflow-hidden relative">

      {/* Background Particles */}
      <BackgroundParticles />
      
      {/* Main Canvas Area with Sidebar */}
      <div className="flex-grow flex overflow-hidden">
        {/* Sidebar */}
        {showSidebar && (
          <div data-onboarding="sidebar">
            <CanvasSidebar
              onAddWidget={(widget) => {
                // Create a proper CanvasWidget from the partial widget
                const newZIndex = maxZIndex + 1;
                const newWidget: CanvasWidget = {
                  id: uuidv4(),
                  type: widget.type || 'default',
                  title: widget.title || 'Untitled Widget',
                  content: widget.content || <div className="flex items-center justify-center h-full text-white">Widget content</div>,
                  position: widget.position || { x: 100, y: 100 },
                  size: widget.size || { width: 350, height: 300 },
                  zIndex: newZIndex,
                  isDeleted: false,
                  groupId: widget.groupId,
                  isParentWidget: widget.isParentWidget,
                  selectedToken: undefined // Explicitly set to undefined for new widgets
                };
                
                console.log('🔧 SIDEBAR: Adding new widget:', newWidget.type, 'with token:', newWidget.selectedToken);
                setWidgets(prevWidgets => {
                  const updatedWidgets = [...prevWidgets, newWidget];
                  console.log('🔧 SIDEBAR: Updated widgets count:', updatedWidgets.length);
                  return updatedWidgets;
                });
                setMaxZIndex(newZIndex);
              }}
              widgetGroups={widgetGroups}
              onShowGroupManager={() => setShowGroupManager(true)}
              isExpanded={isExpanded}
              onToggleExpand={() => setIsExpanded((v) => !v)}
            />
          </div>
        )}
        
        {/* Canvas Container */}
        <div 
          ref={canvasRef}
          className="flex-grow relative overflow-hidden"
          data-onboarding="canvas"
          style={{ 
            cursor: tool === 'hand' ? 'grab' : 'default',
            userSelect: 'none',
            boxShadow: 'inset 0 0 100px rgba(0, 0, 0, 0.05)'
          }}
          onClick={(e) => {
            const isEmptyCanvasClick = e.target === canvasRef.current;
            if (isEmptyCanvasClick) {
              setSelectedWidgetIds([]);
              resetHoverState();
            }

            if (contextMenu.isOpen) {
              setContextMenu({ ...contextMenu, isOpen: false });
            }
          }}
          onContextMenu={(e) => {
            // Check if the right-click is on a widget or interactive element
            const target = e.target as HTMLElement;
            const isWidgetClick = target.closest('.widget') !== null;
            const isInteractiveElement = target.matches('input, button, select, textarea, [role="slider"], .slider, [type="range"], .toggle-slider') ||
                                        target.closest('input, button, select, textarea, [role="slider"], .slider, [type="range"], .toggle-slider') !== null;
            
            // If right-clicking on a widget or interactive element, allow browser's default context menu
            if (isWidgetClick || isInteractiveElement) {
              console.log('🔧 Allowing browser context menu on widget/interactive element');
              return; // Don't prevent default, let browser show its context menu
            }
            
            // Only prevent default and show custom context menu when right-clicking on empty canvas
            e.preventDefault();
            
            resetHoverState();
            
            // Get canvas bounds for proper positioning
            const rect = canvasRef.current?.getBoundingClientRect();
            if (!rect) return;
            
            // Calculate position relative to the canvas container (not the transformed layer)
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;
            
            // Set context menu position relative to the canvas container
            // This ensures it appears at the cursor regardless of zoom/pan
            setContextMenu({
              isOpen: true,
              position: { x, y }
            });
          }}
          onMouseDown={(e) => {
            if (e.button !== 0) return;

            resetHoverState();

            // Determine if we clicked on a widget or widget UI element
            const target = e.target as HTMLElement;
            const isWidgetClick = target.closest('.widget') !== null;
            const isWidgetHeaderClick = target.closest('.widget-header') !== null;
            const isWidgetButtonClick = target.closest('button') !== null;
            const isInteractiveElement = target.matches('input, button, select, textarea, [role="slider"], .slider, [type="range"], .toggle-slider') ||
                                        target.closest('input, button, select, textarea, [role="slider"], .slider, [type="range"], .toggle-slider') !== null;
            
            // Only allow panning if:
            // 1. Tool is "hand" AND we're not clicking a widget UI element OR interactive element
            // 2. Alt key is pressed (BUT NOT on interactive elements)
            // 3. Clicking on empty canvas with select tool
            const isClickOnCanvas = e.target === canvasRef.current || (!isWidgetClick && !isInteractiveElement);
            const allowPan = 
              (tool === 'hand' && !isWidgetHeaderClick && !isWidgetButtonClick && !isInteractiveElement) || 
              (e.altKey && !isInteractiveElement) || 
              (isClickOnCanvas && tool === 'select');

            if (allowPan) {
              setIsDragging(true);
              setDragStart({
                x: e.clientX - panOffset.x,
                y: e.clientY - panOffset.y
              });

              if (canvasRef.current) {
                canvasRef.current.style.cursor = 'grabbing';
              }
            }
          }}

        >
          {/* Light glass overlay for the canvas */}
          <div className="absolute inset-0 pointer-events-none z-0">
            <div className="absolute inset-0 bg-gray-50/80 backdrop-blur-[1px]"></div>
            <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#24c35b]/15 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#24c35b]/15 to-transparent"></div>
          </div>

          {/* Grid Layer - Light and more transparent */}
          <div 
            className="absolute inset-0 pointer-events-none z-[1]"
            style={{
              backgroundSize: `${gridSize}px ${gridSize}px`,
              backgroundImage: `
                linear-gradient(to right, rgba(156, 163, 175, 0.15) 1px, transparent 1px),
                linear-gradient(to bottom, rgba(156, 163, 175, 0.15) 1px, transparent 1px)
              `,
              backgroundPosition: `${panOffset.x % gridSize}px ${panOffset.y % gridSize}px`,
              imageRendering: 'crisp-edges', /* For Firefox */
              willChange: 'background-position'
            }}
          ></div>
          
          {/* Transform Layer */}
          <div 
            className="absolute inset-0 transform-gpu z-[2]"
            style={{ 
              transform: `scale(${zoom}) translate(${panOffset.x / zoom}px, ${panOffset.y / zoom}px)`,
              transformOrigin: '0 0',
              textRendering: 'geometricPrecision',
              WebkitFontSmoothing: 'antialiased',
              MozOsxFontSmoothing: 'grayscale',
              backfaceVisibility: 'hidden'
            }}
          >
            {/* Widget Layer */}
            <WidgetLayer 
              widgets={visibleWidgets}
              onWidgetUpdate={(updatedWidget) => {
                setWidgets(prevWidgets => 
                  prevWidgets.map(widget => 
                    widget.id === updatedWidget.id ? updatedWidget : widget
                  )
                );
              }}
              onWidgetSelect={(widgetId, isMultiSelect) => {
                // Bring widget to front when selected/grabbed
                bringWidgetToFront(widgetId);
                
                setSelectedWidgetIds(prev => {
                  const isAlreadySelected = prev.includes(widgetId);

                  if (isMultiSelect) {
                    return isAlreadySelected
                      ? prev.filter(id => id !== widgetId) 
                      : [...prev, widgetId];
                  } else {
                    if (prev.length === 1 && isAlreadySelected) {
                      return [];
                    } else {
                      return [widgetId];
                    }
                  }
                });
              }}
              onWidgetHover={(isHovering) => {
                setIsHoveringWidgetWithTimeout(isHovering);
              }}
              onWidgetGrab={bringWidgetToFront}
              tool={tool}
              zoom={zoom}
              widgetGroups={widgetGroups}
              selectedWidgetIds={selectedWidgetIds}
              onDuplicateWidget={(widgetId) => {
                const widgetToDuplicate = widgets.find(w => w.id === widgetId);

                if (widgetToDuplicate) {
                  const newWidget: CanvasWidget = {
                    ...widgetToDuplicate,
                    id: uuidv4(),
                    zIndex: maxZIndex + 1,
                    position: {
                      x: widgetToDuplicate.position.x + 20,
                      y: widgetToDuplicate.position.y + 20
                    }
                  };

                  setWidgets(prevWidgets => [...prevWidgets, newWidget]);
                  setMaxZIndex(prev => prev + 1);
                }
              }}
              onMaximizeWidget={() => {}}
              onLockWidget={() => {}}
              onManageGroup={() => setShowGroupManager(true)}
            />
          </div>
          
          {/* Floating Tool Indicator - Only show on mobile */}
          <div className="absolute bottom-4 right-4 px-3 py-1.5 rounded-sm border border-[#24c35b]/30 flex items-center gap-2 text-xs md:hidden bg-white/95 text-gray-900">
            {tool === 'select' ? (
              <>
                <MousePointer size={14} className="text-[#24c35b]" />
                <span>Select</span>
              </>
            ) : (
              <>
                <Hand size={14} className="text-amber-500" />
                <span>Pan</span>
              </>
            )}
          </div>
          
          {/* Selection Control - Show when widgets are selected */}
          {selectedWidgetIds.length > 0 && (
            <div className="absolute bottom-4 left-4 px-3 py-1.5 rounded-sm border border-[#24c35b]/30 flex items-center gap-2 bg-white/95">
                              <span className="text-xs text-gray-900">{selectedWidgetIds.length} selected</span>
              <button 
                className="text-xs bg-gray-500/50 text-gray-300 px-2 py-1 rounded-sm font-medium cursor-not-allowed"
                disabled={true}
                title="Feature currently disabled"
              >
                Manage Group
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Context Menu */}
      {contextMenu.isOpen && (
        <ContextMenu
          position={contextMenu.position}
          onClose={() => setContextMenu({ ...contextMenu, isOpen: false })}
          onAddWidget={(widget) => {
            // Create a proper CanvasWidget from the partial widget
            const newZIndex = maxZIndex + 1;
            const newWidget: CanvasWidget = {
              id: uuidv4(),
              type: widget.type || 'default',
              title: widget.title || 'Untitled Widget',
              content: widget.content || <div className="flex items-center justify-center h-full text-white">Widget content</div>,
              position: widget.position || { x: 100, y: 100 },
              size: widget.size || { width: 350, height: 300 },
              zIndex: newZIndex,
              isDeleted: false,
              groupId: widget.groupId,
              isParentWidget: widget.isParentWidget
            };
            setWidgets(prevWidgets => [...prevWidgets, newWidget]);
            setMaxZIndex(newZIndex);
          }}
          zoom={zoom}
          panOffset={panOffset}
        />
      )}
      
      {/* Widget Group Manager */}
      {showGroupManager && (
        <WidgetGroupManager
          onClose={() => setShowGroupManager(false)}
          widgetGroups={widgetGroups}
          widgets={widgets}
          selectedWidgetIds={selectedWidgetIds}
          onCreateGroup={(group) => {
            const newGroupId = uuidv4();
            setWidgetGroups((prevGroups) => [
              ...prevGroups,
              { ...group, id: newGroupId }
            ]);
            return newGroupId;
          }}
          onUpdateGroup={() => {}}
          onDeleteGroup={() => {}}
          onAddToGroup={() => {}}
          onRemoveFromGroup={() => {}}
        />
      )}
      
      {/* Canvas Bottom Bar */}
      <CanvasBottomBar
        tool={tool}
        onToolChange={(newTool) => {
          setTool(newTool);
        }}
        zoom={zoom}
        onZoomIn={() => {
          const newZoom = Math.min(zoom * 1.2, 5);
          handleZoomChange(newZoom);
        }}
        onZoomOut={() => {
          const newZoom = Math.max(zoom / 1.2, 0.1);
          handleZoomChange(newZoom);
        }}
        onZoomReset={() => {
          handleZoomChange(1);
          handlePanOffsetChange({ x: 0, y: 0 });
        }}
        widgetCount={visibleWidgets.length}
        showSidebar={showSidebar}
        onToggleSidebar={() => {
          console.log('🔥🔥🔥 Canvas TOGGLING SIDEBAR from:', showSidebar, 'to:', !showSidebar);
          setShowSidebar(!showSidebar);
        }}
        isExpanded={isExpanded}
        onToggleExpanded={() => setIsExpanded((v) => !v)}
        showTemplatesPopup={showTemplatesPopup}
        onToggleTemplatesPopup={() => setShowTemplatesPopup(!showTemplatesPopup)}
        onSyncTokens={handleSyncTokens}
        onReplayOnboarding={handleReplayOnboarding}
      />
      
      {/* Templates Popup - Rendered at root level */}
      <TemplatesPopup 
        isOpen={showTemplatesPopup}
        onClose={() => setShowTemplatesPopup(false)}
        onSelectTemplate={(templateType) => {
          console.log(`🎯 Template selected: ${templateType}`);
          
          // Immediately close the templates popup
          setShowTemplatesPopup(false);
          
          // Simple and reliable: just dispatch close events
          window.dispatchEvent(new CustomEvent('ekko:close_all_dropdowns'));
          
          // Clear existing widgets first
          setWidgets([]);
          
          // Reset canvas position to center
          handlePanOffsetChange({ x: 0, y: 0 });
          
          // Auto-zoom for specific templates
          if (templateType === 'sniper-setup' || templateType === 'auto-trading-setup' || templateType === 'copy-trading-setup') {
            handleZoomChange(0.80); // Zoom out to 80%
          } else if (templateType === 'token-trading') {
            handleZoomChange(0.80); // Zoom to 80%
          } else if (templateType === 'token-analysis') {
            handleZoomChange(1.0); // Reset to 100% zoom
          } else if (templateType === 'trading-bots') {
            handleZoomChange(0.75); // Zoom to 75% for bot overview
          } else if (templateType === 'all-widgets') {
            handleZoomChange(0.5); // Zoom to 50% to fit all widgets
          }

          // Define template layouts
          const templateLayouts: Record<string, Array<{ widget: any; position: { x: number; y: number } }>> = {
            'token-trading': [
              { widget: createTradesBuySellWidget(), position: { x: 25, y: 25 } },        // 350px wide
              { widget: createAdvancedChartsWidget(), position: { x: 385, y: 25 } },      // 1200px wide  
              { widget: createTokenInfoWidget(), position: { x: 1495, y: 25 } },          // 400px wide (right side)
              { widget: createActivePositionsWidget(), position: { x: 385, y: 535 } },    // Below the chart (left)
              { widget: createProfitAndLossWidget(), position: { x: 1095, y: 535 } }      // Below the chart (right)
            ],
            'token-analysis': [
              { widget: createTokenInfoWidget(), position: { x: 25, y: 15 } },           // 400px × 800px (left side)
              { widget: createAdvancedChartsWidget(), position: { x: 480, y: 15 } },     // 1200px × 500px (top right)
              { widget: createTokenStatsWidget(), position: { x: 480, y: 520 } }         // 600px × 400px (bottom right)
            ],
            'sniper-setup': [
              { widget: createSniperBotWidget((widget: Partial<CanvasWidget>) => {}), position: { x: 50, y: 50 } },
              { widget: createAdvancedChartsWidget(), position: { x: 460, y: 50 } },
              { widget: createTokenInfoWidget(), position: { x: 1570, y: 50 } },        // 400px × 800px (right of chart)
              { widget: createActivePositionsWidget(), position: { x: 460, y: 560 } },   // Below the chart
              { widget: createProfitAndLossWidget(), position: { x: 1170, y: 560 } }     // Next to Active Positions
            ],
            'auto-trading-setup': [
              { widget: createAdvancedAutoTraderWidget(), position: { x: 50, y: 50 } },
              { widget: createAdvancedChartsWidget(), position: { x: 460, y: 50 } },
              { widget: createTokenInfoWidget(), position: { x: 1570, y: 50 } },        // 400px × 800px (right of chart)
              { widget: createActivePositionsWidget(), position: { x: 460, y: 560 } },   // Below the chart
              { widget: createProfitAndLossWidget(), position: { x: 1170, y: 560 } }     // Next to Active Positions
            ],
            'copy-trading-setup': [
              { widget: createCopyTraderWidget(), position: { x: 50, y: 50 } },
              { widget: createAdvancedChartsWidget(), position: { x: 460, y: 50 } },
              { widget: createTokenInfoWidget(), position: { x: 1570, y: 50 } },        // 400px × 800px (right of chart)
              { widget: createActivePositionsWidget(), position: { x: 460, y: 560 } },   // Below the chart
              { widget: createProfitAndLossWidget(), position: { x: 1170, y: 560 } }     // Next to Active Positions
            ],
            'trading-bots': [
              { widget: createSniperBotWidget((widget: Partial<CanvasWidget>) => {}), position: { x: 50, y: 50 } },
              { widget: createAdvancedAutoTraderWidget(), position: { x: 460, y: 50 } },
              { widget: createCopyTraderWidget(), position: { x: 870, y: 50 } },
              { widget: createProfitAndLossWidget(), position: { x: 50, y: 660 } },
              { widget: createActivePositionsWidget(), position: { x: 440, y: 660 } }
            ],
            'all-widgets': [
              // Top row (y: 50) - Height: 300-400px
              { widget: createProfitAndLossWidget(), position: { x: 50, y: 50 } },          // 380x300
              { widget: createTokenStatsWidget(), position: { x: 450, y: 50 } },            // 600x400 (x: 50+380+20)
              { widget: createTokenInfoWidget(), position: { x: 1070, y: 50 } },            // 400x800 (x: 450+600+20)
              
              // Middle row (y: 470) - Clear space from top row
              { widget: createTradesBuySellWidget(), position: { x: 50, y: 470 } },         // 350x640
              { widget: createAdvancedChartsWidget(), position: { x: 420, y: 470 } },       // 1200x500 (x: 50+350+20)
              { widget: createOrderBookWidget(), position: { x: 1640, y: 470 } },           // 280x500 (x: 420+1200+20)
              
              // Bottom row (y: 990) - Clear space from middle row
              { widget: createSniperBotWidget((widget: Partial<CanvasWidget>) => {}), position: { x: 50, y: 990 } },      // 400x600
              { widget: createCopyTraderWidget(), position: { x: 470, y: 990 } },           // 400x600 (x: 50+400+20)
              { widget: createAdvancedAutoTraderWidget(), position: { x: 890, y: 990 } },   // 400x600 (x: 470+400+20)
              { widget: createActivePositionsWidget(), position: { x: 1310, y: 990 } }      // 700x500 (x: 890+400+20)
            ]
          };

          const layout = templateLayouts[templateType];
          if (layout) {
            console.log(`📦 Creating ${layout.length} widgets for template: ${templateType}`);
            // Add widgets with delay to ensure proper rendering
            layout.forEach((item, index) => {
              setTimeout(() => {
                console.log(`🔧 Creating widget ${index + 1}:`, item.widget.type, 'at position:', item.position);
                const newWidget: CanvasWidget = {
                  id: uuidv4(),
                  type: item.widget.type || 'default',
                  title: item.widget.title || 'Widget',
                  content: item.widget.content || <div className="flex items-center justify-center h-full text-white">Widget content</div>,
                  position: item.position,
                  size: item.widget.size || { width: 350, height: 300 },
                  zIndex: index + 1, // Assign z-index based on order in template
                  isDeleted: false,
                  groupId: item.widget.groupId,
                  isParentWidget: item.widget.isParentWidget
                };
                console.log(`✅ Widget created:`, newWidget.type, newWidget.title);
                setWidgets(prevWidgets => [...prevWidgets, newWidget]);
                
                // Update max z-index after the last widget is created
                if (index === layout.length - 1) {
                  setMaxZIndex(layout.length);
                }
              }, index * 100); // Stagger widget creation
            });
          } else {
            console.error(`❌ No layout found for template: ${templateType}`);
          }
        }}
      />

      {/* Onboarding Guide */}
      <OnboardingGuide
        isOpen={showOnboarding}
        onClose={handleOnboardingClose}
        onSkipAll={handleOnboardingSkipAll}
        canvasRef={ref}
      />
    </div>
  );
});

export default Canvas;